// Sync module: downloads data from the server and imports into local SQLite.
// Handles both full snapshots and incremental delta syncs.

import DB from './db'
import CONSTANTS from 'app/constants'
import cookies from 'js-cookie'

// Tables in the order they should be imported (references first).
var TABLE_ORDER = [
	'user',
	'game',
	'game_version',
	'game_relation',
	'designer',
	'publisher',
	'artist',
	'mechanic',
	'category',
	'family',
	'language',
	'subdomain',
	'game_designer',
	'game_publisher',
	'game_artist',
	'game_mechanic',
	'game_category',
	'game_family',
	'game_language',
	'game_subdomain',
	'groups',
	'buddies',
	'link_group_user',
	'link_buddies_user',
	'collection',
	'play',
	'player',
	'rating',
	'comment',
	'tag',
	'tag_family',
	'tag_attach',
	'list',
	'list_edition',
	'hindex_type',
	'hindex_track',
	'custom_rename',
	'config',
	'preset',
	'view',
	'notification',
	'exchange',
	'generic_settings',
	'devlog',
	'platform_subdomain',
	'platform_externaldomain',
	'platform_early',
	'platform_syncs',
	'platform_boost',
	'platform_voting_feature',
	'platform_voting_vote',
	'log_user_collection',
	'log_user_collection_status',
	'log_user_play',
	'log_user_rating',
	'log_user_hindex',
	'log_user_comment',
	'log_user_tag',
	'log_user_tag_attach',
	'log_user_group',
	'log_user_member',
	'log_user_buddy',
	'log_user_buddies',
	'log_user_listing',
	'log_user_edition',
	'log_user_initiated',
	'log_user_registered',
	'insight',
	'insight_user_collection',
	'insight_user_games',
	'insight_game',
	'insight_game_value',
	'insight_game_version_value',
	'search'
]

function apiHeaders() {
	var headers = { 'content-type': 'application/json' }
	var token = cookies.get('token')
	if (token) headers['Authorization'] = token
	return headers
}

var Sync = {

	// Full snapshot sync. Downloads all data for the current user context.
	// context: { type: 'users'|'groups'|'buddies', id: number, groups: [id, ...] }
	// onProgress: function(table, index, total) - optional progress callback
	snapshot: function(context, onProgress) {
		return window.fetch(CONSTANTS.API + 'offline/snapshot.json', {
			method: 'POST',
			headers: apiHeaders(),
			body: JSON.stringify({
				type: context.type,
				id: context.id,
				name: context.name || null,
				groups: context.groups || [],
				buddies: context.buddies || null,
				minimal: context.minimal ? 1 : 0
			})
		}).then(function(response) {
			if (!response.ok) throw new Error('Snapshot request failed: ' + response.status)
			return response.json()
		}).then(function(snapshot) {
			return DB.init().then(function() {
				return importSnapshot(snapshot, onProgress)
			})
		})
	},

	// Pre-built SQLite snapshot sync. Downloads a server-generated
	// .sqlite file (binary) and mounts it directly into sql.js — no
	// JSON.parse, no per-table importTable. For large users this drops
	// cold-start time from tens of seconds to ~100 ms.
	//
	// The server bakes meta and precomputed_segments into the file as
	// rows in _offline_meta and _precomputed_segments respectively, so
	// this method has no need for a separate JSON sidecar.
	snapshotSqlite: function(context, onProgress) {
		if (onProgress) onProgress('downloading', 0, 0)
		return window.fetch(CONSTANTS.API + 'offline/snapshot_sqlite', {
			method: 'POST',
			headers: apiHeaders(),
			body: JSON.stringify({
				type: context.type,
				id: context.id,
				name: context.name || null,
				groups: context.groups || [],
				buddies: context.buddies || null,
				minimal: context.minimal ? 1 : 0
			})
		}).then(function(response) {
			if (!response.ok) throw new Error('Sqlite snapshot fetch failed: ' + response.status)
			return response.arrayBuffer()
		}).then(function(buf) {
			if (onProgress) onProgress('mounting', 0, 0)
			return DB.initFromBuffer(new Uint8Array(buf))
		}).then(function() {
			// Rehydrate precomputed segments from the embedded side table.
			var Insights = require('offline/insights').default || require('offline/insights')
			if (Insights && typeof Insights.setPrecomputed === 'function') {
				var segments = {}
				try {
					var rows = DB.query('SELECT name, data FROM _precomputed_segments')
					for (var i = 0; i < rows.length; i++) {
						try { segments[rows[i].name] = JSON.parse(rows[i].data) } catch (e) { /* skip malformed segment row */ }
					}
				} catch (e) {
					// table missing — older snapshot, no precomputed
				}
				Insights.setPrecomputed(segments)
			}
			var Config = require('offline/model/config').default
			if (Config && typeof Config.invalidate === 'function') Config.invalidate()
			if (onProgress) onProgress('done', 1, 1)
		})
	},

	// File-based snapshot for large users. Triggers server-side write, polls until ready, then downloads.
	// context: same as snapshot(), plus context.pollInterval (ms, default 5000)
	snapshotAsync: function(context, onProgress) {
		var pollInterval = context.pollInterval || 5000

		// Step 1: Trigger the write
		return window.fetch(CONSTANTS.API + 'offline/snapshot.json', {
			method: 'POST',
			headers: apiHeaders(),
			body: JSON.stringify({
				type: context.type,
				id: context.id,
				name: context.name || null,
				groups: context.groups || [],
				buddies: context.buddies || null,
				minimal: context.minimal ? 1 : 0,
				write_file: 1
			})
		}).then(function(response) {
			if (!response.ok) throw new Error('Snapshot write request failed: ' + response.status)
			return response.json()
		}).then(function(writeResult) {
			if (!writeResult.status) throw new Error('Snapshot write failed: ' + JSON.stringify(writeResult))
			if (onProgress) onProgress('write_started', 0, 0)

			// Step 2: Poll for readiness
			function poll() {
				return window.fetch(CONSTANTS.API + 'offline/snapshot.json?type=' + encodeURIComponent(context.type) + '&id=' + context.id, {
					method: 'GET',
					headers: apiHeaders()
				}).then(function(response) {
					return response.json()
				}).then(function(status) {
					if (status.snapshot === 'ready') {
						if (onProgress) onProgress('file_ready', 0, 0)
						// Step 3: Download and import
						return window.fetch(CONSTANTS.API + 'offline/snapshot.json?type=' + encodeURIComponent(context.type) + '&id=' + context.id + '&download=1', {
							method: 'GET',
							headers: apiHeaders()
						}).then(function(dlResponse) {
							if (!dlResponse.ok) throw new Error('Snapshot download failed: ' + dlResponse.status)
							return dlResponse.json()
						}).then(function(snapshot) {
							return DB.init().then(function() {
								return importSnapshot(snapshot, onProgress)
							})
						})
					}
					// Still pending — wait and retry
					if (onProgress) onProgress('polling', 0, 0)
					return new Promise(function(resolve) {
						setTimeout(function() { resolve(poll()) }, pollInterval)
					})
				})
			}

			return poll()
		})
	},

	// Delta sync. Downloads only changes since the last sync.
	delta: function(context, onProgress) {
		var lastVersion = DB.meta('data_version_' + context.type + '_' + context.id) || '0'

		return window.fetch(CONSTANTS.API + 'offline/delta.json', {
			method: 'POST',
			headers: apiHeaders(),
			body: JSON.stringify({
				type: context.type,
				id: context.id,
				since_version: parseInt(lastVersion, 10)
			})
		}).then(function(response) {
			if (!response.ok) throw new Error('Delta request failed: ' + response.status)
			return response.json()
		}).then(function(delta) {
			return DB.init().then(function() {
				return importDelta(delta, onProgress)
			})
		})
	},

	// Import a raw data object directly (useful for testing or manual import).
	importRaw: function(data, onProgress) {
		return DB.init().then(function() {
			return importSnapshot(data, onProgress)
		})
	},

	// Get the locally stored data_version for a context.
	getVersion: function(type, id) {
		return parseInt(DB.meta('data_version_' + type + '_' + id) || '0', 10)
	},

	// Check if a sync is needed by comparing local vs remote data_version.
	checkFreshness: function(context) {
		var localVersion = Sync.getVersion(context.type, context.id)

		return window.fetch(CONSTANTS.API + 'offline/version.json', {
			method: 'POST',
			headers: apiHeaders(),
			body: JSON.stringify({
				type: context.type,
				id: context.id
			})
		}).then(function(response) {
			return response.json()
		}).then(function(result) {
			return {
				local: localVersion,
				remote: result.data_version || 0,
				fresh: localVersion >= (result.data_version || 0)
			}
		})
	}

}

function importSnapshot(snapshot, onProgress) {
	var tables = Object.keys(snapshot.tables || snapshot)
	var ordered = TABLE_ORDER.filter(function(t) { return tables.indexOf(t) !== -1 })
	// Include any tables not in our predefined order
	tables.forEach(function(t) { if (ordered.indexOf(t) === -1) ordered.push(t) })

	var total = ordered.length
	var chain = Promise.resolve()

	ordered.forEach(function(table, index) {
		chain = chain.then(function() {
			if (onProgress) onProgress(table, index, total)

			var rows = snapshot.tables ? snapshot.tables[table] : snapshot[table]
			if (!rows || !rows.length) return

			DB.truncate(table)
			DB.importTable(table, rows)
		})
	})

	return chain.then(function() {
		// Store sync metadata
		var now = Math.floor(Date.now() / 1000)
		DB.meta('last_sync', now)

		if (snapshot.meta) {
			if (snapshot.meta.data_version) {
				DB.meta('data_version_' + snapshot.meta.type + '_' + snapshot.meta.id, snapshot.meta.data_version)
			}
			if (snapshot.meta.user_id) {
				DB.meta('sync_user_id', snapshot.meta.user_id)
			}
		}

		if (onProgress) onProgress('done', total, total)

		return DB.save()
	})
}

function importDelta(delta, onProgress) {
	var operations = delta.operations || []
	var total = operations.length

	operations.forEach(function(op, index) {
		if (onProgress) onProgress(op.table, index, total)

		if (op.action === 'upsert' && op.rows && op.rows.length) {
			DB.importTable(op.table, op.rows)
		} else if (op.action === 'delete' && op.ids && op.ids.length) {
			var key = op.key || (op.table + '_id')
			DB.run('DELETE FROM ' + op.table + ' WHERE ' + key + ' IN (' + op.ids.join(',') + ')')
		} else if (op.action === 'truncate') {
			DB.truncate(op.table)
		}
	})

	// Update metadata
	var now = Math.floor(Date.now() / 1000)
	DB.meta('last_sync', now)

	if (delta.meta && delta.meta.data_version) {
		DB.meta('data_version_' + delta.meta.type + '_' + delta.meta.id, delta.meta.data_version)
	}

	if (onProgress) onProgress('done', total, total)

	return DB.save()
}

export default Sync
